import { useMemo } from 'react';
import { Mountain, AlertCircle, ExternalLink } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Chip } from '@/ui/primitives';
import { useImovelSolo, type SoloClasse } from '../api';

interface Props {
  imovelId: string | number | null;
}

// Paleta SiBCS-inspired (12 ordens principais). Mantida estável por ordem
// para que a mesma classe sempre saia na mesma cor entre imóveis diferentes.
const ORDEM_COLORS: Record<string, string> = {
  LATOSSOLO: '#c9805a',
  ARGISSOLO: '#d4a373',
  NEOSSOLO: '#a3845b',
  CAMBISSOLO: '#9c6b3f',
  GLEISSOLO: '#5d8a8b',
  PLINTOSSOLO: '#b8474e',
  NITOSSOLO: '#7a3b2e',
  ESPODOSSOLO: '#6b7e8f',
  CHERNOSSOLO: '#3a3a3a',
  LUVISSOLO: '#a07856',
  ORGANOSSOLO: '#4a3c2e',
  VERTISSOLO: '#5e4a3a',
};
const ORDEM_FALLBACK = '#8b7a6b';

function colorForOrdem(ordem: string | null | undefined): string {
  if (!ordem) return ORDEM_FALLBACK;
  const key = ordem.toUpperCase().split(/\s+/)[0] ?? '';
  return ORDEM_COLORS[key] ?? ORDEM_FALLBACK;
}

interface RingProjector {
  toX: (lon: number) => number;
  toY: (lat: number) => number;
  width: number;
  height: number;
}

function makeProjector(
  bbox: [number, number, number, number],
  width: number,
  height: number,
  pad = 8,
): RingProjector {
  const [xmin, ymin, xmax, ymax] = bbox;
  const dx = xmax - xmin || 1e-6;
  const dy = ymax - ymin || 1e-6;
  const sx = (width - 2 * pad) / dx;
  const sy = (height - 2 * pad) / dy;
  const s = Math.min(sx, sy);
  const offsetX = pad + (width - 2 * pad - dx * s) / 2;
  const offsetY = pad + (height - 2 * pad - dy * s) / 2;
  return {
    toX: (lon) => offsetX + (lon - xmin) * s,
    toY: (lat) => offsetY + (ymax - lat) * s,
    width,
    height,
  };
}

function ringsToPath(rings: number[][][], proj: RingProjector): string {
  return rings
    .map((ring) =>
      ring
        .map((pt, i) => {
          const lon = pt[0] ?? 0;
          const lat = pt[1] ?? 0;
          const x = proj.toX(lon).toFixed(2);
          const y = proj.toY(lat).toFixed(2);
          return `${i === 0 ? 'M' : 'L'}${x},${y}`;
        })
        .join(' ') + ' Z',
    )
    .join(' ');
}

// Extrai todos os anéis (Polygon ou MultiPolygon) num array uniforme.
function geomToRings(g: unknown): number[][][] {
  if (!g || typeof g !== 'object') return [];
  const obj = g as { type?: string; coordinates?: unknown };
  if (obj.type === 'Polygon') return (obj.coordinates as number[][][]) ?? [];
  if (obj.type === 'MultiPolygon') {
    const polys = (obj.coordinates as number[][][][]) ?? [];
    return polys.flat();
  }
  return [];
}

export function SoloPedologiaSection({ imovelId }: Props) {
  const { data, isLoading, isError, error } = useImovelSolo(imovelId);

  const svg = useMemo(() => {
    if (!data) return null;
    const W = 360;
    const H = 240;
    const proj = makeProjector(data.car.bbox, W, H);
    const carRings = geomToRings(data.car.geojson);
    const carPath = carRings.length ? ringsToPath(carRings, proj) : null;
    const layers = data.solo.classes.map((c) => ({
      classe: c,
      path: ringsToPath(geomToRings(c.geojson), proj),
      fill: colorForOrdem(c.ordem ?? c.componente),
    }));
    return { W, H, carPath, layers };
  }, [data]);

  if (!imovelId) return null;

  if (isLoading) {
    return (
      <div className="text-text-muted text-sm py-4">Carregando análise de solo…</div>
    );
  }

  if (isError) {
    const msg = error instanceof Error ? error.message : 'Erro ao consultar solo';
    return (
      <div className="flex items-start gap-2 text-text-muted text-sm py-3">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <div>
          <div>Solo não disponível para este imóvel.</div>
          <div className="text-xs mt-0.5 opacity-70">{msg}</div>
        </div>
      </div>
    );
  }

  if (!data || !data.solo.classes.length) {
    return (
      <div className="text-text-muted text-sm py-4">
        Sem dados de pedologia para o polígono CAR.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-4 flex-wrap">
        {svg && svg.carPath && (
          <div
            className="rounded-lg border border-border bg-surface2 shrink-0"
            style={{ width: svg.W, height: svg.H }}
          >
            <svg
              width={svg.W}
              height={svg.H}
              viewBox={`0 0 ${svg.W} ${svg.H}`}
              role="img"
              aria-label="Mapa de solos do imóvel"
            >
              <path
                d={svg.carPath}
                fill="rgba(255,255,255,0.04)"
                stroke="rgba(0,0,0,0.25)"
                strokeWidth={1.5}
              />
              {svg.layers.map((l, i) => (
                <path
                  key={i}
                  d={l.path}
                  fill={l.fill}
                  fillOpacity={0.78}
                  stroke={l.fill}
                  strokeOpacity={0.95}
                  strokeWidth={0.6}
                />
              ))}
              <path
                d={svg.carPath}
                fill="none"
                stroke="rgba(0,0,0,0.55)"
                strokeWidth={1.2}
              />
            </svg>
          </div>
        )}

        <div className="flex-1 min-w-[260px] space-y-3">
          <div className="flex items-center gap-2 flex-wrap text-xs text-text-muted">
            <Mountain size={13} />
            <span>
              Fonte: IBGE · Pedologia <strong>{data.solo.escala}</strong> · ano{' '}
              {data.solo.ano_referencia}
            </span>
            <span className="opacity-60">·</span>
            <span>
              {data.solo.total_ha.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ha
              cobertos de {data.car.area_ha.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}{' '}
              ha (CAR)
            </span>
          </div>

          <Link
            to="/imoveis/$id/solo"
            params={{ id: String(imovelId) }}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline"
          >
            <ExternalLink size={12} />
            Abrir mapa completo
          </Link>

          <ul className="space-y-2">
            {data.solo.classes.map((c, i) => (
              <SoloClasseRow key={i} c={c} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function SoloClasseRow({ c }: { c: SoloClasse }) {
  const cor = colorForOrdem(c.ordem ?? c.componente);
  const titulo =
    [c.ordem, c.subordem, c.grande_grupo].filter(Boolean).join(' · ') ||
    c.componente ||
    'Não classificado';
  const detalhes = [c.textura, c.relevo].filter(Boolean).join(' · ');
  return (
    <li className="flex items-start gap-3 p-2 rounded-md border border-border bg-surface">
      <span
        aria-hidden
        className="mt-1 h-3 w-3 rounded-sm shrink-0"
        style={{ background: cor }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-tight break-words">{titulo}</div>
        {detalhes && (
          <div className="text-xs text-text-muted mt-0.5">{detalhes}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm tabular-nums font-medium">
          {c.ha.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ha
        </div>
        <Chip tone="neutral" className="mt-0.5 text-[11px]">
          {c.pct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
        </Chip>
      </div>
    </li>
  );
}
